import {
  Alert,
  Button,
  Card,
  Code,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
  Checkbox,
  Divider,
  Badge,
  Switch,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { runInference } from "../../services/inference";
import { usePatientStore } from "../../stores/patientStore";
import {
  IconBulb,
  IconPlayerPlay,
  IconSparkles,
  IconHeartRateMonitor,
  IconVaccine,
  IconPhoto,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useECGFeatures } from "../../hooks/useECGFeatures";
import { useInsulinData } from "../../hooks/useInsulinData";
import { useMealImages } from "../../hooks/useMealImages";
import type { ECGFeatures } from "../../types/api";

// Utility functions for data analysis
function analyzeECGInsulinCorrelations(
  ecgData: ECGFeatures[],
  insulinData: any[],
) {
  const correlations: any = {};

  // Find temporal correlations between insulin doses and heart rate changes
  const insulinEvents = insulinData.map((insulin) => ({
    timestamp: new Date(insulin.recordedAt || insulin.timestamp),
    type: insulin.type,
    units: insulin.units,
  }));

  const ecgEvents = ecgData.map((ecg) => ({
    timestamp: new Date(ecg.timestamp),
    heartRate: ecg.heartRate,
    hrv: ecg.hrvFeatures.heartRateVariability,
    rhythmClassification: ecg.waveformFeatures.rhythmClassification,
  }));

  // Calculate heart rate changes after insulin administration
  const heartRateChangesAfterInsulin: number[] = [];
  insulinEvents.forEach((insulin) => {
    const ecgBefore = ecgEvents.find(
      (ecg) =>
        ecg.timestamp < insulin.timestamp &&
        insulin.timestamp.getTime() - ecg.timestamp.getTime() <= 30 * 60 * 1000, // Within 30 minutes before
    );
    const ecgAfter = ecgEvents.find(
      (ecg) =>
        ecg.timestamp > insulin.timestamp &&
        ecg.timestamp.getTime() - insulin.timestamp.getTime() <= 60 * 60 * 1000, // Within 1 hour after
    );

    if (ecgBefore && ecgAfter) {
      heartRateChangesAfterInsulin.push(
        ecgAfter.heartRate - ecgBefore.heartRate,
      );
    }
  });

  if (heartRateChangesAfterInsulin.length > 0) {
    const avgChange =
      heartRateChangesAfterInsulin.reduce((sum, change) => sum + change, 0) /
      heartRateChangesAfterInsulin.length;
    correlations.averageHeartRateChangeAfterInsulin =
      Math.round(avgChange * 100) / 100;
    correlations.insulinEventsAnalyzed = heartRateChangesAfterInsulin.length;
  }

  return correlations;
}

function calculateDataCompleteness(data: any[]): number {
  if (!data || data.length === 0) return 0;

  const totalFields = data.length * 5; // Assume 5 key fields per record
  const filledFields = data.reduce((count, record) => {
    let recordFields = 0;
    if (record.timestamp) recordFields++;
    if (record.patientId) recordFields++;
    if (record.id) recordFields++;
    // Add more field checks based on data type
    return count + recordFields;
  }, 0);

  return Math.round((filledFields / totalFields) * 100);
}

function calculateTemporalOverlap(
  ecgData: ECGFeatures[],
  insulinData: any[],
): number {
  if (!ecgData.length || !insulinData.length) return 0;

  const ecgTimeRange = {
    start: new Date(
      Math.min(...ecgData.map((d) => new Date(d.timestamp).getTime())),
    ),
    end: new Date(
      Math.max(...ecgData.map((d) => new Date(d.timestamp).getTime())),
    ),
  };

  const insulinTimeRange = {
    start: new Date(
      Math.min(
        ...insulinData.map((d) =>
          new Date(d.recordedAt || d.timestamp).getTime(),
        ),
      ),
    ),
    end: new Date(
      Math.max(
        ...insulinData.map((d) =>
          new Date(d.recordedAt || d.timestamp).getTime(),
        ),
      ),
    ),
  };

  const overlapStart = new Date(
    Math.max(ecgTimeRange.start.getTime(), insulinTimeRange.start.getTime()),
  );
  const overlapEnd = new Date(
    Math.min(ecgTimeRange.end.getTime(), insulinTimeRange.end.getTime()),
  );

  if (overlapEnd <= overlapStart) return 0;

  const totalDuration = Math.max(
    ecgTimeRange.end.getTime() - ecgTimeRange.start.getTime(),
    insulinTimeRange.end.getTime() - insulinTimeRange.start.getTime(),
  );

  const overlapDuration = overlapEnd.getTime() - overlapStart.getTime();

  return Math.round((overlapDuration / totalDuration) * 100);
}

const STRATEGY_OPTIONS = [
  { value: "generic", label: "Generic" },
  { value: "contextual", label: "Contextual" },
  { value: "structured", label: "Structured JSON" },
  { value: "few-shot", label: "Few-shot" },
  { value: "dynamic", label: "Dynamic" },
  { value: "ecg-analysis", label: "ECG Analysis" },
  { value: "insulin-optimization", label: "Insulin Optimization" },
  { value: "correlation-analysis", label: "Multi-Data Correlation" },
  { value: "arrhythmia-detection", label: "Arrhythmia Detection" },
  { value: "hrv-analysis", label: "HRV Analysis" },
  { value: "dosage-timing", label: "Dosage Timing Analysis" },
] as const;

const PROVIDER_OPTIONS = [
  { value: "auto", label: "Auto (Gemini -> OpenAI fallback)" },
  { value: "gemini", label: "Gemini 1.5 Flash" },
  { value: "openai", label: "OpenAI gpt-4o-mini" },
] as const;

export function ExperimentsPage(): JSX.Element {
  const selectedPatientId = usePatientStore((state) => state.selectedPatientId);
  const summaries = usePatientStore((state) => state.summaries);

  // Data hooks for enhanced analysis
  const {
    data: ecgData,
    isLoading: ecgLoading,
    getECGSummary,
  } = useECGFeatures(selectedPatientId);
  const {
    data: insulinData,
    isLoading: insulinLoading,
    getInsulinSummary,
  } = useInsulinData(selectedPatientId);
  const {
    data: mealImages,
    isLoading: mealsLoading,
    getMealImagesSummary,
  } = useMealImages(selectedPatientId);

  // Data type selection state
  const [includeECG] = useState(true);
  const [includeInsulin, setIncludeInsulin] = useState(true);
  const [includeMealImages, setIncludeMealImages] = useState(false);

  // Time range selection (coming soon)
  const [startDate] = useState<Date | null>(null);
  const [endDate] = useState<Date | null>(null);

  // Data preprocessing options
  const [preprocessData, setPreprocessData] = useState(true);
  const [includeCorrelations, setIncludeCorrelations] = useState(true);

  const defaultContext = useMemo(() => {
    if (!selectedPatientId) return {};

    const baseContext = summaries[selectedPatientId] ?? {};
    const enhancedContext: any = { ...baseContext };

    // Add ECG data if selected and available
    if (includeECG && ecgData && ecgData.length > 0) {
      const ecgSummary = getECGSummary();
      if (ecgSummary) {
        enhancedContext.ecgAnalysis = {
          summary: {
            totalRecordings: ecgSummary.totalRecordings,
            averageHeartRate: ecgSummary.averageHeartRate,
            averageHRV: ecgSummary.averageHRV,
            arrhythmiaCount: ecgSummary.arrhythmiaCount,
            normalRhythmCount: ecgSummary.normalRhythmCount,
            rhythmClassifications: ecgSummary.rhythmClassifications,
          },
          recentData: ecgData.slice(-5).map((ecg) => ({
            timestamp: ecg.timestamp,
            heartRate: ecg.heartRate,
            hrv: ecg.hrvFeatures.heartRateVariability,
            rhythmClassification: ecg.waveformFeatures.rhythmClassification,
            rmssd: ecg.hrvFeatures.rmssd,
            sdnn: ecg.hrvFeatures.sdnn,
          })),
        };
      }
    }

    // Add insulin data if selected and available
    if (includeInsulin && insulinData && insulinData.length > 0) {
      const insulinSummary = getInsulinSummary();
      if (insulinSummary) {
        enhancedContext.insulinAnalysis = {
          summary: {
            totalMeasurements: insulinSummary.totalMeasurements,
            totalUnits: insulinSummary.totalUnits,
            rapidUnits: insulinSummary.rapidUnits,
            longActingUnits: insulinSummary.longActingUnits,
            averageUnitsPerDay:
              Math.round(insulinSummary.averageUnitsPerDay * 100) / 100,
          },
          recentData: insulinData.slice(-10).map((insulin) => ({
            timestamp: insulin.recordedAt,
            units: insulin.units,
            type: insulin.type,
            injectionSite: insulin.injectionSite,
            notes: insulin.notes,
          })),
        };
      }
    }

    // Add meal image metadata if selected and available
    if (includeMealImages && mealImages && mealImages.length > 0) {
      const mealsSummary = getMealImagesSummary();
      if (mealsSummary) {
        enhancedContext.mealImageAnalysis = {
          summary: {
            totalImages: mealsSummary.totalImages,
            totalSizeMB: mealsSummary.totalSizeMB,
            imagesWithDescriptions: mealsSummary.imagesWithDescriptions,
            formatCounts: mealsSummary.formatCounts,
          },
          recentData: mealImages.slice(-5).map((meal) => ({
            timestamp: meal.timestamp,
            mealId: meal.mealId,
            hasDescription: !!meal.metadata?.description,
            format: meal.metadata?.format,
            sizeMB: meal.metadata?.size
              ? Math.round((meal.metadata.size / (1024 * 1024)) * 100) / 100
              : 0,
          })),
        };
      }
    }

    // Add correlation analysis if enabled
    if (
      includeCorrelations &&
      includeECG &&
      includeInsulin &&
      ecgData &&
      insulinData
    ) {
      enhancedContext.correlations = {
        ecgInsulin: analyzeECGInsulinCorrelations(ecgData, insulinData),
        dataQuality: {
          ecgCompleteness: calculateDataCompleteness(ecgData),
          insulinCompleteness: calculateDataCompleteness(insulinData),
          temporalOverlap: calculateTemporalOverlap(ecgData, insulinData),
        },
      };
    }

    return enhancedContext;
  }, [
    selectedPatientId,
    summaries,
    includeECG,
    includeInsulin,
    includeMealImages,
    ecgData,
    insulinData,
    mealImages,
    getECGSummary,
    getInsulinSummary,
    getMealImagesSummary,
    includeCorrelations,
  ]);

  const [strategy, setStrategy] = useState<string>("contextual");
  const [provider, setProvider] =
    useState<(typeof PROVIDER_OPTIONS)[number]["value"]>("auto");
  const [temperature, setTemperature] = useState(0.4);
  const [contextText, setContextText] = useState(
    JSON.stringify(defaultContext, null, 2),
  );

  useEffect(() => {
    setContextText(JSON.stringify(defaultContext, null, 2));
  }, [defaultContext]);

  const mutation = useMutation({
    mutationKey: ["prompt-run"],
    mutationFn: async () => {
      if (!selectedPatientId) {
        throw new Error("Select a patient before running inference");
      }

      // Validate that at least one data type is selected
      if (!includeECG && !includeInsulin && !includeMealImages) {
        throw new Error("Select at least one data type for analysis");
      }

      let payload: Record<string, any>;
      try {
        payload = JSON.parse(contextText || "{}");
      } catch {
        throw new Error("Invalid JSON in context. Please check the format.");
      }

      // Add metadata about the analysis configuration
      payload.analysisConfig = {
        dataTypes: {
          ecg: includeECG,
          insulin: includeInsulin,
          mealImages: includeMealImages,
        },
        timeRange: {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        },
        preprocessing: preprocessData,
        correlations: includeCorrelations,
        strategy,
      };

      return runInference({
        patientId: selectedPatientId,
        strategy: strategy as any,
        provider,
        context: payload,
        temperature,
      });
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : "Inference failed";
      notifications.show({
        color: "red",
        title: "Inference error",
        message,
      });
    },
  });

  return (
    <Stack gap="xl">
      <Group justify="space-between">
        <div>
          <Title order={2}>Prompt Experiments</Title>
          <Text c="dimmed">
            Iterate on prompt templates and providers, with automatic logging to
            the D1 prompt_runs table.
          </Text>
        </div>
        <Button
          leftSection={<IconPlayerPlay size={16} />}
          loading={mutation.isPending}
          disabled={
            !selectedPatientId ||
            mutation.isPending ||
            (ecgLoading && includeECG) ||
            (insulinLoading && includeInsulin) ||
            (mealsLoading && includeMealImages) ||
            (!includeECG && !includeInsulin && !includeMealImages)
          }
          onClick={() => mutation.mutate()}
        >
          Run Inference
        </Button>
      </Group>

      {!selectedPatientId && (
        <Alert color="yellow" icon={<IconBulb size={18} />}>
          Choose a patient on the dashboard to seed the prompt context.
        </Alert>
      )}

      {/* Data Visualization Panel */}
      {selectedPatientId &&
        (includeECG || includeInsulin || includeMealImages) && (
          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <Title order={4}>Data Being Analyzed</Title>

              {includeECG && (
                <Stack gap="xs">
                  <Group>
                    <IconHeartRateMonitor size={16} />
                    <Text size="sm" fw={500}>
                      ECG Features
                    </Text>
                    {ecgLoading && <Badge color="blue">Loading...</Badge>}
                  </Group>
                  {ecgData.length > 0 && !ecgLoading && (
                    <Group>
                      <Text size="xs" c="dimmed">
                        {ecgData.length} recordings • Avg HR:{" "}
                        {getECGSummary()?.averageHeartRate || "N/A"} bpm • Avg
                        HRV: {getECGSummary()?.averageHRV || "N/A"}
                      </Text>
                    </Group>
                  )}
                </Stack>
              )}

              {includeInsulin && (
                <Stack gap="xs">
                  <Group>
                    <IconVaccine size={16} />
                    <Text size="sm" fw={500}>
                      Insulin Data
                    </Text>
                    {insulinLoading && <Badge color="blue">Loading...</Badge>}
                  </Group>
                  {insulinData.length > 0 && !insulinLoading && (
                    <Group>
                      <Text size="xs" c="dimmed">
                        {insulinData.length} measurements • Total units:{" "}
                        {getInsulinSummary()?.totalUnits || "N/A"} • Avg/day:{" "}
                        {getInsulinSummary()?.averageUnitsPerDay || "N/A"}
                      </Text>
                    </Group>
                  )}
                </Stack>
              )}

              {includeMealImages && (
                <Stack gap="xs">
                  <Group>
                    <IconPhoto size={16} />
                    <Text size="sm" fw={500}>
                      Meal Images
                    </Text>
                    {mealsLoading && <Badge color="blue">Loading...</Badge>}
                  </Group>
                  {mealImages.length > 0 && !mealsLoading && (
                    <Group>
                      <Text size="xs" c="dimmed">
                        {mealImages.length} images •
                        {getMealImagesSummary()?.totalSizeMB || "N/A"} MB •
                        {getMealImagesSummary()?.imagesWithDescriptions ||
                          "N/A"}{" "}
                        with descriptions
                      </Text>
                    </Group>
                  )}
                </Stack>
              )}

              {includeCorrelations && includeECG && includeInsulin && (
                <Alert color="blue" variant="light">
                  <Text size="sm">
                    Correlation analysis enabled: Analyzing relationships
                    between ECG metrics and insulin administration patterns.
                  </Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        )}

      {/* Data Selection Panel */}
      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <Title order={4}>Data Selection</Title>

          <Group>
            <Checkbox
              label="Insulin Data"
              checked={includeInsulin}
              onChange={(event) =>
                setIncludeInsulin(event.currentTarget.checked)
              }
              disabled={insulinLoading || insulinData.length === 0}
            />
            <Checkbox
              label="ECG Data"
              checked={includeECG}
              disabled={ecgLoading || ecgData.length === 0}
            />
            <Checkbox
              label="Insulin Data"
              checked={includeInsulin}
              onChange={(event) =>
                setIncludeInsulin(event.currentTarget.checked)
              }
              disabled={insulinLoading || insulinData.length === 0}
            />
            <Checkbox
              label="Meal Images"
              checked={includeMealImages}
              onChange={(event) =>
                setIncludeMealImages(event.currentTarget.checked)
              }
              disabled={mealsLoading || mealImages.length === 0}
            />
            <Checkbox
              label="Meal Images"
              checked={includeMealImages}
              onChange={(event) =>
                setIncludeMealImages(event.currentTarget.checked)
              }
              disabled={mealsLoading || mealImages.length === 0}
            />
          </Group>

          <Group>
            <Switch
              label="Include Correlations"
              checked={includeCorrelations}
              onChange={(event) =>
                setIncludeCorrelations(event.currentTarget.checked)
              }
              disabled={!includeECG || !includeInsulin}
            />
            <Switch
              label="Preprocess Data"
              checked={preprocessData}
              onChange={(event) =>
                setPreprocessData(event.currentTarget.checked)
              }
            />
          </Group>

          <Divider />

          {/* Data Quality Indicators */}
          <Group>
            <Text size="sm" fw={500}>
              Data Quality:
            </Text>
            {includeECG && (
              <Badge
                color={ecgData.length > 0 ? "green" : "red"}
                variant="light"
              >
                ECG: {ecgData.length} records
              </Badge>
            )}
            {includeInsulin && (
              <Badge
                color={insulinData.length > 0 ? "green" : "red"}
                variant="light"
              >
                Insulin: {insulinData.length} records
              </Badge>
            )}
            {includeMealImages && (
              <Badge
                color={mealImages.length > 0 ? "green" : "red"}
                variant="light"
              >
                Images: {mealImages.length} records
              </Badge>
            )}
          </Group>

          {/* Time Range Selection */}
          <Group>
            <Text size="sm" c="dimmed">
              Date range selection coming soon
            </Text>
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <Group grow>
            <Select
              label="Prompt strategy"
              data={STRATEGY_OPTIONS}
              value={strategy}
              onChange={(value) =>
                setStrategy(
                  (value as (typeof STRATEGY_OPTIONS)[number]["value"]) ??
                    "contextual",
                )
              }
            />
            <Select
              label="Provider"
              data={PROVIDER_OPTIONS}
              value={provider}
              onChange={(value) =>
                setProvider(
                  (value as (typeof PROVIDER_OPTIONS)[number]["value"]) ??
                    "auto",
                )
              }
            />
            <NumberInput
              label="Temperature"
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              onChange={(value) => setTemperature(Number(value))}
            />
          </Group>

          <Textarea
            minRows={12}
            spellCheck={false}
            value={contextText}
            onChange={(event) => setContextText(event.currentTarget.value)}
            label="Prompt context (JSON)"
            description="Pre-populated with the latest patient summary and selected data types."
          />
        </Stack>
      </Paper>

      {mutation.data && (
        <Card withBorder padding="lg" radius="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconSparkles size={20} />
              <div>
                <Text fw={600}>Model output</Text>
                <Text size="sm" c="dimmed">
                  {mutation.data.provider} - {mutation.data.model} -{" "}
                  {mutation.data.result.latencyMs}ms - $
                  {mutation.data.result.costUsd.toFixed(6)}
                </Text>
              </div>
            </Group>

            {/* Analysis Summary */}
            <Group>
              <Text size="sm" fw={500}>
                Prompt tokens: {mutation.data.result.promptTokens} - Completion
                tokens: {mutation.data.result.completionTokens}
              </Text>
            </Group>

            {/* Data Types Analyzed */}
            <Group>
              <Text size="sm" fw={500}>
                Data Types Analyzed:
              </Text>
              {includeECG && (
                <Badge size="xs" color="red">
                  ECG
                </Badge>
              )}
              {includeInsulin && (
                <Badge size="xs" color="blue">
                  Insulin
                </Badge>
              )}
              {includeMealImages && (
                <Badge size="xs" color="green">
                  Meal Images
                </Badge>
              )}
              {includeCorrelations && (
                <Badge size="xs" color="purple">
                  Correlations
                </Badge>
              )}
            </Group>

            {/* Strategy Information */}
            <Group>
              <Text size="sm" c="dimmed">
                Strategy: <strong>{strategy}</strong>
                {preprocessData && " • Preprocessed"}
                {startDate &&
                  endDate &&
                  ` • Date range: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`}
              </Text>
            </Group>

            <Paper radius="md" p="md" bg="gray.0">
              <Text component="pre" style={{ whiteSpace: "pre-wrap" }}>
                {mutation.data.result.content}
              </Text>
            </Paper>

            <Code block>
              {JSON.stringify(
                {
                  runId: mutation.data.id,
                  createdAt: mutation.data.metadata.createdAt,
                  analysisConfig: {
                    dataTypes: {
                      ecg: includeECG,
                      insulin: includeInsulin,
                      mealImages: includeMealImages,
                    },
                    timeRange: {
                      startDate: startDate?.toISOString(),
                      endDate: endDate?.toISOString(),
                    },
                    preprocessing: preprocessData,
                    correlations: includeCorrelations,
                    strategy,
                  },
                },
                null,
                2,
              )}
            </Code>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
